import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { companyApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import { Button, Card, CardHeader, ConfirmDialog, FileUploader, useToast } from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { ACCENT_VAR, accentStyle } from '@/utils/branding';

import { useApplyBranding } from './cache';

export const BRAND_IMAGE_TYPES = 'image/jpeg,image/png,image/webp';
export const MAX_BRAND_IMAGE_BYTES = 5 * 1024 * 1024;

/** The wide photo across the top of the company page. Saved on upload. */
export function CoverEditor({ branding, readOnly = false }) {
  const toast = useToast();
  const applyBranding = useApplyBranding();
  const [confirming, setConfirming] = useState(false);

  const upload = useMutation({
    mutationFn: (file) => companyApi.uploadCover(file),
    onSuccess: (updated) => {
      applyBranding(updated);
      toast.success('Cover photo updated');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not upload the cover photo')),
  });

  const remove = useMutation({
    mutationFn: () => companyApi.removeCover(),
    onSuccess: (updated) => {
      applyBranding(updated);
      setConfirming(false);
      toast.success('Cover photo removed');
    },
    onError: (error) => toast.error(errorMessage(error, 'Could not remove the cover photo')),
  });

  return (
    <Card>
      <CardHeader
        title="Cover photo"
        description="A wide photo of your team or office. Without one, the page uses your accent colour."
        action={
          !readOnly &&
          branding.cover_url && (
            <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
              Remove
            </Button>
          )
        }
      />
      {branding.cover_url ? (
        <img
          src={resolveFileUrl(branding.cover_url)}
          alt="Current cover"
          className="border-line mb-4 aspect-[4/1] w-full rounded-lg border object-cover"
        />
      ) : (
        <div
          aria-hidden="true"
          className="mb-4 aspect-[4/1] w-full rounded-lg"
          style={{ ...accentStyle(branding.accent_color), background: ACCENT_VAR }}
        />
      )}
      {!readOnly && (
        <FileUploader
          accept={BRAND_IMAGE_TYPES}
          maxSizeBytes={MAX_BRAND_IMAGE_BYTES}
          isUploading={upload.isPending}
          hint="JPEG, PNG or WebP, up to 5 MB. Wide images (4:1) look best."
          onSelect={(file) => upload.mutate(file)}
        />
      )}

      <ConfirmDialog
        open={confirming}
        title="Remove the cover photo?"
        message="The page will show your accent colour instead."
        confirmLabel="Remove"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirming(false)}
      />
    </Card>
  );
}
